<template>
  <div
    :class="{ cases: content.cases?.length, 'is-dragging': isDragging }"
    class="image-slider"
  >
    <div class="wrapper image-slider-inner">
      <span
        v-if="content.headline"
        class="section-label image-slider__headline"
      >
        {{ content.headline }}
      </span>
      <div ref="root" class="image-slider-container">
        <GPicture
          v-if="content.pictures?.length"
          v-for="(picture, index) in content.pictures"
          :key="index"
          class="image-slider__figure"
          :content="{ picture }"
        />

        <CaseCard
          v-if="content.cases?.length"
          v-for="item in content.cases"
          :key="item.title"
          class="image-slider__case"
          :content="{ ...item, picture: item.picture?.sets?.['640'] }"
        />
      </div>

      <div v-if="flickity" class="image-slider__footer">
        <span ref="$description" class="image-slider__footer__description">
          {{ description }}
        </span>
        <div class="image-slider__controls">
          <button
            class="image-slider__controls__prev"
            :disabled="flickity.selectedIndex === 0"
            @click="flickity.previous()"
          >
            <SvgSprite
              :aria-label="t('global-previous')"
              symbol="ui-full-arrow"
              size="20"
            />
          </button>
          <span ref="$number" class="image-slider__controls__number">
            {{ number }}
          </span>
          <span class="image-slider__controls__separator"></span>
          <span class="image-slider__controls__total">
            {{ numbers[itemLength - 1] }}
          </span>
          <button
            class="image-slider__controls__next"
            :disabled="flickity.selectedIndex === itemLength - 1"
            @click="flickity.next()"
          >
            <SvgSprite
              :aria-label="t('global-next')"
              symbol="ui-full-arrow"
              size="20"
            />
          </button>
        </div>
      </div>
    </div>
    <div v-if="!content.cases?.length" class="wrapper"><hr /></div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, ref } from 'vue'
import { gsap } from 'gsap'
import { useResize } from '@/utils/aware'

import { useChromeStore } from '@/stores/chrome'
import CaseCard, { type Content as CardContent } from './CaseCard.vue'
import { Picture } from '@/types'
import { debounce } from 'debounce'

type Content = {
  headline?: string
  pictures?: Picture[]
  cases: ({ picture: Picture } & CardContent)[]
}

const props = defineProps({
  content: {
    type: Object as PropType<Content>,
    required: true,
  },
})

const root = ref<Element>()
const chrome = useChromeStore()

const lang = chrome.language

const numberLists = {
  en: [
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
    'eleven',
    'twelve',
    'thirteen',
    'fourteen',
    'fifteen',
    'sixteen',
    'seventeen',
    'eighteen',
    'nineteen',
    'twenty',
  ],
  fr: [
    'un',
    'deux',
    'trois',
    'quatre',
    'cinq',
    'six',
    'sept',
    'huit',
    'neuf',
    'dix',
    'onze',
    'douze',
    'treize',
    'quatorze',
    'quinze',
    'seize',
    'dix-sept',
    'dix-huit',
    'dix-neuf',
    'vingt',
  ],
}

const numbers = computed(() => numberLists[lang])

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const flickity = ref()
const number = ref('')
const $number = ref({} as HTMLElement)
const $description = ref({} as HTMLElement)
const description = ref(props.content.pictures?.[0].caption || '')

const itemLength = computed(
  () => props.content.pictures?.length || props.content.cases?.length || 0
)

const { t } = useChromeStore()

const isDragging = ref(false)
let timeout: ReturnType<typeof setTimeout>
const initFlickity = () => {
  import('flickity').then(Flickity => {
    flickity.value = new Flickity.default(root.value as Element, {
      contain: true,
      setGallerySize: Boolean(props.content.cases?.length),
      cellAlign: 'left',
      pageDots: false,
      prevNextButtons: false,
    })

    // Links ignore pointer events while dragging, so releasing the mouse
    // over the link a drag started on doesn't trigger a click.
    // Reset in a timeout so the class is still applied when the click lands.
    flickity.value.on('dragStart', () => {
      clearTimeout(timeout)
      isDragging.value = true
    })
    flickity.value.on('dragEnd', () => {
      timeout = setTimeout(() => (isDragging.value = false))
    })

    flickity.value.on('change', async () => {
      await gsap.to([$number.value, $description.value], {
        duration: 0.2,
        yPercent: -100,
        opacity: 0,
      })

      number.value = numbers.value[flickity.value.selectedIndex]
      description.value =
        props.content.pictures?.[flickity.value.selectedIndex].caption || ''

      gsap.fromTo(
        [$number.value, $description.value],
        { yPercent: 100, opacity: 0 },
        {
          duration: 0.2,
          yPercent: 0,
          opacity: 1,
        }
      )
    })
    useResize(() => {
      flickity.value && flickity.value.resize()
    })

    number.value = numbers.value[flickity.value.selectedIndex]
  })
}

onMounted(() => {
  initFlickity()
})
</script>

<style lang="scss">
@import 'flickity/css/flickity.css';
</style>

<style lang="scss" scoped>
/* stylelint-disable no-descending-specificity */
/* stylelint-disable declaration-no-important */

.image-slider {
  overflow: hidden;
}

.image-slider-inner {
  // prettier-ignore
  @include fluid(padding-bottom,(xxs: 40px, xxl: 80px));
  // prettier-ignore
  @include fluid(gap,(xxs: 20px, xxl: 40px));

  display: flex;
  flex-direction: column;

  .cases & {
    padding-bottom: 0;
  }
}

.image-slider__headline {
  text-align: center;
}

.image-slider-container {
  // prettier-ignore
  @include fluid(height,(xxs: 210px, xxxl: 715px));

  display: flex;

  &.disabled {
    pointer-events: none;
  }

  :deep(.flickity-viewport) {
    overflow: visible;
    width: 100%;
    height: 100%;
  }

  .cases & {
    height: auto;

    :deep(.flickity-slider) {
      display: flex;
    }
  }
}

.image-slider__figure {
  position: absolute;
  width: auto !important;
  height: 100%;
  perspective: none;
  pointer-events: auto;

  :deep(.picture) {
    height: 100%;
  }

  :deep(.picture__img) {
    width: auto;
    max-width: none;
    transform: scale(0.9);
    transition: transform 0.25s;
  }

  :deep(figcaption) {
    display: none !important;
  }

  :deep(img) {
    width: auto;
    height: 100%;
  }

  &.is-selected {
    :deep(.picture__img) {
      transform: scale(1);
      box-shadow: 0 0 30px 0 rgba($c-black, 0.2);
    }
  }
}

.image-slider__case {
  position: absolute !important;
  width: min(90vw, 50rem);
  aspect-ratio: 25 / 19;

  .is-dragging & {
    pointer-events: none;
  }

  &:not(:last-child) {
    margin-right: 3rem;
  }
}

.image-slider__img {
  @include image-fit;
}

.image-slider__footer {
  display: flex;
  flex-direction: column;

  @include mq(s) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
}

.image-slider__footer__description {
  margin-bottom: 2rem;
  font-size: 13px;

  @include mq(l) {
    width: col(8);
  }

  @include mq(s) {
    margin-bottom: 0;
  }
}

.image-slider__controls {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  min-width: 30rem;

  .cases & {
    justify-content: flex-end;
  }
}

.image-slider__controls__separator {
  flex: 1;
  margin: 0 1rem;
  border-top: 1px solid var(--c-foreground);
}

.image-slider__controls__next {
  margin-left: 2rem;

  .cases & {
    margin-left: 0;
  }
}

.image-slider__controls__prev {
  margin-right: 2rem;

  svg {
    transform: rotate(180deg);
  }
}

.image-slider__controls__next,
.image-slider__controls__prev {
  margin-bottom: -5px;
  padding: 0;
  line-height: 1;
  background-color: transparent;
  border: 0;
  fill: var(--c-foreground);
  transition: opacity 0.15s;

  &:disabled {
    opacity: 0.5;
  }

  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
}

.image-slider__controls__number,
.image-slider__controls__separator,
.image-slider__controls__total {
  .cases & {
    display: none;
  }
}

.image-slider__controls__number {
  @extend %fw-black;

  // prettier-ignore
  @include fluid(font-size,(xxs: 14px, xxl: 20px));

  // prettier-ignore
  @include fluid(line-height,(xxs: 14px, xxl: 20px));

  margin-bottom: 3px;
  font-family: $ff-alt;
  line-height: 1;
  letter-spacing: 0.5px;
}

.image-slider__controls__total {
  @extend %fw-semi;

  font-size: 1rem;
  line-height: 1.3rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.5;
}
</style>
